import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { VehicleField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateVehicle';
import { Vehicle } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetEstimate';
import { TFunction } from 'i18next';
import { Moment } from 'moment';
import { updateVehicle } from 'domains/estimate/Estimate.store';
import { Flex, FloatDatePicker, FloatInput, LightCard, Lowercase, Text } from 'UI';
import { getMomentDate, getStringDateFromMoment } from 'utils/date';
import { formatVehicleBrand } from 'utils/mappers';
import { isQueryBMM } from 'utils/validateQuery';
import { SVehicleGrid } from './VehicleSection.styled';

export interface VehicleSectionProps {
  vehicle: Vehicle;
}

function getVehicleTitle(vehicle: Vehicle) {
  if (vehicle.catalogSource === 'DATAHUB') {
    return vehicle.dataHubVehicle?.name;
  }
  if (vehicle.catalogSource === 'IAM') {
    return [formatVehicleBrand(vehicle.vehicleBrand), vehicle.model].filter(Boolean).join(' ');
  }
  return undefined;
}

function catalogSourceSwitch(vehicle: Vehicle, t: TFunction<'translation'>) {
  switch (vehicle.catalogSource) {
    case 'DATAHUB':
      return (
        <>
          <Text type="base" color="grey35">
            {vehicle.dataHubVehicle?.modelType ?? ''}
          </Text>
          <Text type="base" color="grey35" transform={'capitalize'}>
            <Lowercase>{vehicle.dataHubVehicle?.engine ?? ''}</Lowercase>
          </Text>
          <Text type="base" color="grey35" transform={'capitalize'}>
            <Lowercase>{vehicle.dataHubVehicle?.gearbox ?? ''}</Lowercase>
          </Text>
        </>
      );
    case 'IAM':
      return (
        <>
          <Text type="base" color="grey35">{`${vehicle.iamVehicle?.bodyType ?? ''} ${
            vehicle.iamVehicle?.doors ? vehicle.iamVehicle.doors + t('catalog.vehicle.doors', ' Doors') : ''
          }`}</Text>
          <Text type="base" color="grey35">{`${vehicle.iamVehicle?.engineCodes ?? ''} ${
            vehicle.iamVehicle?.energy ?? ''
          }`}</Text>
        </>
      );
    default:
      return null;
  }
}

const VehicleSection = ({ vehicle }: VehicleSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const getManufacturingDate = useCallback((manufacturingDate: string) => getMomentDate(manufacturingDate) ?? null, []);

  const handleUpdate = (field: VehicleField) => (newValue: string) => {
    dispatch(updateVehicle({ field, newValue }));
  };

  const handleDateUpdateUpdate = (field: VehicleField) => (newValue: Moment | null) => {
    dispatch(updateVehicle({ field, newValue: getStringDateFromMoment(newValue) ?? '' }));
  };

  const vehicleTitle = getVehicleTitle(vehicle);

  return (
    <LightCard title={vehicleTitle || t('common.vehicle', 'Vehicle')}>
      <Flex direction={['column', 'row']} gap={8}>
        <Flex direction={'column'}>
          <SVehicleGrid>
            <FloatInput
              initialValue={vehicle.vin}
              onChangeDebounced={handleUpdate('VIN')}
              placeholder={t('catalog.vehicle.vin', 'VIN')}
              ellipsis
              readOnly={!isQueryBMM(vehicle.vehicleKey)}
              bordered={isQueryBMM(vehicle.vehicleKey)}
            />
            <FloatInput
              initialValue={vehicle.vrn}
              onChangeDebounced={handleUpdate('VRN')}
              placeholder={t('catalog.vehicle.vrn', 'VRN')}
              ellipsis
              bordered
            />
            <FloatDatePicker
              value={getManufacturingDate(vehicle.manufacturingDate)}
              onChange={handleDateUpdateUpdate('MANUFACTURING_DATE')}
              placeholder={t('catalog.vehicle.manufacturing_date', 'Manufacturing date')}
              ellipsis
            />
            <FloatInput
              initialValue={vehicle.mileage}
              onChangeDebounced={handleUpdate('MILEAGE')}
              placeholder={t('catalog.vehicle.mileage', 'Mileage')}
              ellipsis
              bordered
            />
          </SVehicleGrid>
        </Flex>
        <Flex size={0} direction={'column'} minWidth={200}>
          {catalogSourceSwitch(vehicle, t)}
        </Flex>
      </Flex>
    </LightCard>
  );
};

export default VehicleSection;
